import axios from "axios";
import { API_PATHS, URL_PATH } from "./Constants/constants";
import { clearSession, createSession, getSessionIdToken, updateSessionToken } from "./Auth/session";

export const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

axiosInstance.interceptors.request.use((config) => {
  const { token } = getSessionIdToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Response Interceptor: Refresh token on 401
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    console.log(error.code);

    // Check for status code 401
    // if (
    //   error.response?.status === 401 &&
    //   !originalRequest._retry &&
    //   !originalRequest.url.includes()
    // ) {

    // Replace this if statement with above statement (Currently API does not return 401)
    if (
      error.code === "ERR_NETWORK" &&
      !originalRequest._retry &&
      !originalRequest.url.includes(API_PATHS.GetRefreshToken)
    ) {
      originalRequest._retry = true;

      // Add incoming requests to Queue while refreshing token
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return axiosInstance(originalRequest);
        });
      }

      isRefreshing = true;

      try {
        const { refreshToken } = getSessionIdToken();

        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}${API_PATHS.GetRefreshToken}`,
          { RefreshToken: refreshToken }
        );
        if (!response.data.IsSuccess) {
          processQueue(error, null);
          clearSession(); // Optional: logout on refresh failure
          return Promise.reject(error);
        }
        const newAccessToken = response.data.Response.Token;
        const newRefreshToken = response.data.Response.TokenRefresh;
        updateSessionToken(newAccessToken, newRefreshToken);

        processQueue(null, newAccessToken);

        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return axiosInstance(originalRequest);
      } catch (err) {
        processQueue(err, null);
        clearSession(); // Optional: logout on refresh failure
        // window.location.href = URL_PATH.login;
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export const getApiResponse = async (path) => {
  let config = {};
  try {
    const response = await axiosInstance.get(path, config);
    if (!response.data.IsSuccess) {
      return {
        status: 500,
        data: null,
        message: response.Message,
      };
    }
    return {
      status: response.status,
      data: response.data.Response ?? {},
      message: response.data.Message,
    };
  } catch (error) {
    console.error(error);
    return {
      status: error.code === "ERR_NETWORK" ? 401 : 500,
      data: null,
      message: error.message,
    };
  }
};

export const postData = async (path, data, requiredField) => {
  let config = {
    headers: {
      "Content-Type": requiredField?.formData
        ? "multipart/form-data"
        : "application/json",
    },
  };

  try {
    const response = await axiosInstance.post(path, data, config);
    return {
      status: response.status,
      data: response.data ?? {},
    };
  } catch (error) {
    console.error(error);
    return {
      status: error.code === "ERR_NETWORK" ? 401 : 500,
      message: error.message,
    };
  }
};
